import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowUpDown, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Fuel, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { useData } from '@/state/data';
import { useChartDays, useMachineSparklines } from '@/state/useChartDays';
import { can, roleLabel } from '@/domain/roles';
import { fmtNumber } from '@/domain/numbers';
import { formatDateTime, formatRange } from '@/domain/time';
import { FIELD_LABELS, MACHINE_TYPE_LABELS, PROVENANCE_LABELS } from '@/lib/labels';
import { machineLabel, presentText } from '@/lib/present';
import { InlineMetric, KpiStrip, KpiStripCell } from '@/components/app/Kpi';
import { MetricExplainDialog } from '@/components/app/MetricExplainDialog';
import { DailyCharts, ShareBar, Sparkline, CHART } from '@/components/app/Charts';
import { EmptyState, Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { CoverageBadge, MachineRef, Note, SeverityBadge, SourceStatusBadge } from '@/components/app/Badges';
import { cn } from '@/lib/utils';

/*
  Produksjonsanalyse (sekundær på Oversikt, primær i Produksjon/Sammenlign). Innholdet er den tidligere
  Oversikt-flaten: nøkkeltall mot referanseperiode, trend, prioriterte observasjoner og maskintabell.
  På arbeidsstarten vises bare en kompakt rad – resten ligger bak «Vis full analyse» (?analyse=1).
*/

/* ------------------------------------------------------------------ */
/* Prioriterte observasjoner (maks tre, med kildehenvisning)            */
/* ------------------------------------------------------------------ */
function buildFindings(c, cmp, deviations, linkTo, machinesById) {
  const out = [];
  const nameOf = (id) => machinesById.get(id)?.fictionalName ?? '';
  for (const d of deviations.slice(0, 3)) {
    out.push({
      id: d.id,
      severity: d.severity,
      title: d.ruleLabel,
      subject: d.scope.machineId ? `${machineLabel(d.scope.machineId)} · ${nameOf(d.scope.machineId)}` : d.scope.sourceId ? presentText(d.scope.sourceId) : 'Linje 1 (samlet)',
      text: presentText(d.observed),
      sources: d.evidenceRecordIds.length ? `${d.evidenceRecordIds.length} kildeposter` : 'Kildestatus',
      to: linkTo(`/avvik/${encodeURIComponent(d.id)}`),
      testId: `finding-link-${d.id}`,
    });
  }
  if (out.length < 3) {
    const topFuel = [...c.machines].filter((m) => m.inScope).sort((a, b) => b.fuelLitres - a.fuelLitres)[0];
    if (topFuel && topFuel.fuelLitres > 0) {
      out.push({ id: 'topfuel', severity: 'info', title: 'Største dieselbidrag', subject: `${machineLabel(topFuel.machineId)} · ${nameOf(topFuel.machineId)}`, text: `${fmtNumber(topFuel.fuelLitres, 0)} L${topFuel.fuelShare !== null ? ` (${fmtNumber(topFuel.fuelShare, 1)} % av tilordnet diesel)` : ''}`, sources: `${topFuel.intervalIds.length} kildeposter`, to: linkTo(`/maskiner/${topFuel.machineId}`) });
    }
  }
  if (out.length < 3) {
    const topIdle = [...c.machines].filter((m) => m.inScope).sort((a, b) => b.idleHours - a.idleHours)[0];
    if (topIdle && topIdle.idleHours > 0 && !out.some((o) => o.subject?.startsWith(machineLabel(topIdle.machineId)))) {
      out.push({ id: 'topidle', severity: 'info', title: 'Størst tomgang', subject: `${machineLabel(topIdle.machineId)} · ${nameOf(topIdle.machineId)}`, text: `${fmtNumber(topIdle.idleHours, 2)} h${topIdle.idleShare !== null ? ` (${fmtNumber(topIdle.idleShare, 1)} % av egne motortimer)` : ''}`, sources: `${topIdle.intervalIds.length} kildeposter`, to: linkTo(`/maskiner/${topIdle.machineId}`, { vis: 'tomgang' }) });
    }
  }
  if (out.length < 3 && cmp.byKey.productionRate.status === 'ok') {
    const r = cmp.byKey.productionRate;
    out.push({ id: 'rate', severity: 'info', title: 'Produksjonstakt mot referanse', subject: 'Linje 1', text: `${fmtNumber(c.productionRate.value, 0)} t/h (${Math.abs(r.pctChange) < 1 ? 'uendret' : `${r.pctChange > 0 ? '+' : ''}${fmtNumber(r.pctChange, 1)} %`})`, sources: `${c.lineStates.length} linjetilstander`, to: linkTo('/sammenlign') });
  }
  return out.slice(0, 3);
}

/** Kompakt datadekning-status. Klikk åpner Datakilder. */
export function CoverageStatus({ ok, summary, sources, onClick }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          data-testid="overview-coverage-chip"
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-[10px] border px-3 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]',
            ok ? 'border-[var(--hr-hairline)] bg-white text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]' : 'border-[rgba(154,91,0,0.3)] bg-[var(--hr-amber-bg)] text-[var(--hr-amber)] hover:bg-[#FFEFD2]',
          )}
        >
          {ok ? <CheckCircle2 className="h-4 w-4 text-[var(--hr-green)]" aria-hidden="true" /> : <AlertTriangle className="h-4 w-4" aria-hidden="true" />}
          {ok ? 'Datadekning komplett' : 'Datadekning delvis'}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="space-y-1 text-[13px]">
          <div>{summary}</div>
          {sources.map((s) => (
            <div key={s.sourceId} className="flex items-center gap-1.5">
              <SourceStatusBadge status={s.status} /> {s.name.split(' (')[0]} · sist {formatDateTime(s.succeededAt)}
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/* ------------------------------------------------------------------ */
/* Full analyse (nøkkeltall, trend, observasjoner, maskiner)            */
/* ------------------------------------------------------------------ */
function ProductionAnalysisBody({ r, c, cmp, chartDays, sparks, findings, machinesById, showCosts, role, co2Note, setExplainKey }) {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('fuel');
  const inScope = c.machines.filter((m) => m.inScope);
  const machines = [...inScope].sort((a, b) => (sortBy === 'fuel' ? b.fuelLitres - a.fuelLitres : b.idleHours - a.idleHours));
  const maxFuel = Math.max(1, ...inScope.map((m) => m.fuelLitres));
  const selectedDates = c.dates;

  return (
    <div className="space-y-6 border-t border-[var(--hr-hairline)] px-4 py-5 sm:px-5" data-testid="production-analysis-body">
      <section aria-label="Nøkkeltall" className="space-y-4">
        <KpiStrip data-testid="kpi-row-primary">
          <KpiStripCell metric={c.tonnes} row={cmp.byKey.tonnes} invertGood onExplain={() => setExplainKey('tonnes')} testKey="tonnes" spark={chartDays.map((d) => (d.kind === 'planned_off' ? null : d.tonnes))} sparkLabel="Produsert per dag" sparkColor={CHART.bar} />
          <KpiStripCell metric={c.dieselPerTonne} row={cmp.byKey.dieselPerTonne} onExplain={() => setExplainKey('dieselPerTonne')} testKey="diesel-per-tonne" spark={chartDays.map((d) => d.litresPerTonne)} sparkLabel="Diesel per tonn per dag" />
          <KpiStripCell metric={c.machineHoursPerTonne} row={cmp.byKey.machineHoursPerTonne} onExplain={() => setExplainKey('machineHoursPerTonne')} testKey="machine-hours-per-tonne" />
          <KpiStripCell metric={c.idleShare} row={cmp.byKey.idleShare} onExplain={() => setExplainKey('idleShare')} testKey="idle-share" spark={chartDays.map((d) => d.idleShare)} sparkLabel="Tomgangsandel per dag" sparkColor="#234090" />
        </KpiStrip>
        <div className="hr-surface grid grid-cols-2 gap-px overflow-hidden bg-[var(--hr-hairline)] lg:grid-cols-5" data-testid="kpi-row-secondary">
          <InlineMetric metric={c.co2TotalKg} row={cmp.byKey.co2TotalKg} onExplain={() => setExplainKey('co2TotalKg')} testKey="co2-total" note={co2Note} />
          <InlineMetric metric={c.co2PerTonne} row={cmp.byKey.co2PerTonne} onExplain={() => setExplainKey('co2PerTonne')} testKey="co2-per-tonne" note={co2Note} />
          <InlineMetric metric={c.costPerTonne} row={cmp.byKey.costPerTonne} onExplain={() => setExplainKey('costPerTonne')} testKey="cost-per-tonne" hidden={!showCosts} hiddenText={`Skjult for rollen ${roleLabel(role)}`} />
          <InlineMetric metric={c.productionRate} row={cmp.byKey.productionRate} invertGood onExplain={() => setExplainKey('productionRate')} testKey="production-rate" />
          <InlineMetric metric={c.stopHours} row={cmp.byKey.stopHours} onExplain={() => setExplainKey('stopHours')} testKey="stop-hours" />
          <div className="bg-white lg:hidden" aria-hidden="true" />
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-8">
          <DailyCharts days={chartDays} selectedDates={selectedDates} onSelectDay={(d) => r.setPeriod(d, d)} title="Produksjon og diesel per tonn" description="Klikk en dag for å filtrere alle paneler." />
        </div>
        <aside className="min-w-0 lg:col-span-4">
          <Panel className="flex h-full flex-col" data-testid="top-findings-panel">
            <PanelHeader title="Undersøk først" description="Rangert etter alvorlighet." />
            {findings.length === 0 ? (
              <p className="px-5 py-5 text-sm text-[var(--hr-muted)]">Ingen funn i perioden.</p>
            ) : (
              <ol className="flex-1 divide-y divide-[var(--hr-hairline)]">
                {findings.map((f, i) => (
                  <li key={f.id} data-testid={`finding-${i + 1}`}>
                    <Link to={f.to} data-testid={`finding-${i + 1}-link`} className="group flex gap-3.5 px-5 py-3.5 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]">
                      <span className="hr-num mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--hr-inset)] text-[13px] font-semibold text-[var(--hr-heading)]">{i + 1}</span>
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-sm font-semibold leading-5 text-[var(--hr-heading)]">{f.title}</span>
                          <SeverityBadge severity={f.severity} />
                        </div>
                        <div className="text-[13px] text-[var(--hr-muted)]">{f.subject}</div>
                        <p className="hr-tabular text-sm leading-6 text-[var(--hr-text)]">{f.text}</p>
                        <div className="flex items-center justify-between gap-3 pt-0.5 text-[13px] text-[var(--hr-muted-2)]">
                          <span>{f.sources}</span>
                          <span className="inline-flex items-center gap-1 font-medium text-[var(--hr-heading)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100">
                            Åpne <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
            )}
            <div className="border-t border-[var(--hr-hairline)] px-5 py-3">
              <Link to={r.linkTo('/avvik')} className="inline-flex items-center gap-1 text-sm font-medium text-[var(--hr-heading)] underline-offset-[3px] hover:underline" data-testid="overview-all-deviations-link">
                Alle avvik i perioden <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Panel>
        </aside>
      </div>

      <Panel data-testid="overview-machines-panel">
        <PanelHeader
          title="Maskiner"
          description={`Sortert etter ${sortBy === 'fuel' ? 'diesel' : 'tomgang'} · trend siste ${sparks.days.length} dager`}
          actions={
            <>
              <div className="inline-flex rounded-[10px] border border-[var(--hr-border)] bg-white p-0.5" role="group" aria-label="Sorter maskiner">
                <Button variant={sortBy === 'fuel' ? 'default' : 'ghost'} size="sm" className="h-8 rounded-[8px] px-2.5 text-[13px]" onClick={() => setSortBy('fuel')} data-testid="machines-sort-fuel" aria-pressed={sortBy === 'fuel'}>
                  <Fuel className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Diesel
                </Button>
                <Button variant={sortBy === 'idle' ? 'default' : 'ghost'} size="sm" className="h-8 rounded-[8px] px-2.5 text-[13px]" onClick={() => setSortBy('idle')} data-testid="machines-sort-idle" aria-pressed={sortBy === 'idle'}>
                  <Timer className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Tomgang
                </Button>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to={r.linkTo('/maskiner')} data-testid="overview-all-machines-link">
                  Alle maskiner <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </Button>
            </>
          }
        />
        <div className="hr-scroll overflow-x-auto">
          <Table data-testid="overview-machines-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Maskin</TableHead>
                <TableHead className="text-right">
                  <span className="inline-flex items-center gap-1">Diesel (L) {sortBy === 'fuel' && <ArrowUpDown className="h-3 w-3" aria-hidden="true" />}</span>
                </TableHead>
                <TableHead className="hidden md:table-cell">Andel av diesel</TableHead>
                <TableHead className="text-right">
                  <span className="inline-flex items-center gap-1">Tomgang (h) {sortBy === 'idle' && <ArrowUpDown className="h-3 w-3" aria-hidden="true" />}</span>
                </TableHead>
                <TableHead className="text-right">Tomgangsandel</TableHead>
                <TableHead className="hidden lg:table-cell">Trend diesel</TableHead>
                <TableHead className="hidden sm:table-cell">Status</TableHead>
                <TableHead className="w-12 pr-5"><span className="sr-only">Åpne</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {machines.map((m) => {
                const meta = machinesById.get(m.machineId);
                const sp = sparks.byMachine[m.machineId];
                const to = r.linkTo(`/maskiner/${m.machineId}`);
                const hot = m.idleShare !== null && m.idleShare > 25;
                return (
                  <TableRow
                    key={m.machineId}
                    data-testid={`overview-machine-row-${m.machineId}`}
                    className="group cursor-pointer"
                    onClick={(e) => {
                      if (e.target.closest('a')) return;
                      navigate(to);
                    }}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && navigate(to)}
                  >
                    <TableCell className="pl-5">
                      <MachineRef machineId={m.machineId} name={meta?.fictionalName ?? m.name} to={to} testId={`overview-machine-link-${m.machineId}`} />
                      <div className="mt-0.5 text-[13px] text-[var(--hr-muted)]">{MACHINE_TYPE_LABELS[m.type]}</div>
                    </TableCell>
                    <TableCell className="hr-num text-right text-[15px] font-semibold text-[var(--hr-heading)]">{fmtNumber(m.fuelLitres, 0)}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2.5">
                        <ShareBar value={m.fuelLitres} max={maxFuel} label={`Andel ${m.fuelShare === null ? '–' : `${fmtNumber(m.fuelShare, 1)} %`}`} width={96} />
                        <span className="hr-tabular text-[13px] text-[var(--hr-muted)]">{m.fuelShare === null ? '–' : `${fmtNumber(m.fuelShare, 1)} %`}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hr-num text-right">{fmtNumber(m.idleHours, 2)}</TableCell>
                    <TableCell className="text-right">
                      {m.idleShare === null ? (
                        <span className="text-[var(--hr-muted)]">–</span>
                      ) : (
                        <span className={cn('hr-num inline-flex items-center gap-1.5 font-medium', hot ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-text)]')}>
                          {hot && <span className="h-1.5 w-1.5 rounded-full bg-[var(--hr-amber)]" aria-hidden="true" />}
                          {fmtNumber(m.idleShare, 1)} %
                          {hot && <span className="sr-only"> – over 25 %</span>}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">{sp ? <Sparkline values={sp.fuel} label={`Diesel per dag, ${machineLabel(m.machineId)}`} color={CHART.bar} width={104} height={28} /> : <span className="text-[13px] text-[var(--hr-muted)]">–</span>}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {m.quality === 'partial' ? <CoverageBadge status="partial" label={`Delvis · ${m.missingCount} felt`} /> : m.quality === 'complete' ? <CoverageBadge status="complete" label="Komplett" /> : <CoverageBadge status="not_applicable" label="Ingen data" />}
                    </TableCell>
                    <TableCell className="pr-5 text-right">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--hr-muted-2)] transition-colors duration-150 group-hover:bg-[var(--hr-inset)] group-hover:text-[var(--hr-heading)]" aria-hidden="true">
                        <ChevronRight className="h-4 w-4" />
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              {machines.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-sm text-[var(--hr-muted)]">Ingen maskindata i perioden.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-[var(--hr-hairline)] px-5 py-3 text-[13px] text-[var(--hr-muted)]">
          <span className="hr-tabular">
            Sum: {fmtNumber(c.engineHours.value, 1)} h motortimer · {fmtNumber(c.idleHours.value, 1)} h tomgang · {fmtNumber(c.fuelLitres.value, 0)} L diesel{c.fuelLitres.status === 'partial' ? ' (registrert, delvis)' : ''}
          </span>
          <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[var(--hr-amber)]" aria-hidden="true" /> Tomgangsandel over 25 %</span>
        </div>
      </Panel>
    </div>
  );
}

/**
 * Sekundært panel på Oversikt: kompakt rad med de tre viktigste forholdstallene og «Vis full analyse».
 * Produksjonsfilteret (periode/scenario) styres fra Produksjon – her vises perioden som tekst med lenke.
 */
export function ProductionAnalysisPanel() {
  const r = useResults();
  const { role } = useSession();
  const { sources } = useData();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [explainKey, setExplainKey] = useState(null);
  const chartDays = useChartDays(r.dataset, r.query);
  const sparks = useMachineSparklines(r.dataset, r.query);

  const forklar = r.sp.get('forklar');
  const expanded = params.get('analyse') === '1' || !!forklar;
  const setExpanded = (open) => {
    const next = new URLSearchParams(params);
    if (open) next.set('analyse', '1');
    else next.delete('analyse');
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (forklar && r.ready) {
      setExplainKey(forklar);
      r.set({ forklar: null }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forklar, r.ready]);

  const { ready, current: curResult, comparison, deviations, linkTo, dataset } = r;
  const machinesById = useMemo(() => new Map((dataset?.machines ?? []).map((m) => [m.machineId, m])), [dataset]);
  const findings = useMemo(() => (ready && curResult ? buildFindings(curResult, comparison, deviations, linkTo, machinesById) : []), [ready, curResult, comparison, deviations, linkTo, machinesById]);

  if (!r.ready) {
    return (
      <Panel data-testid="oversikt-production-secondary-card">
        <PanelHeader eyebrow="Sekundært" title="Produksjonsanalyse" description="Henter produksjonsdata …" />
        <div className="p-5"><SkeletonTable rows={2} /></div>
      </Panel>
    );
  }
  const { current: c, comparison: cmp } = r;
  const showCosts = can(role, 'view_costs');
  const explainMetric = explainKey ? c[explainKey] : null;
  const plannedOff = c.tonnes.reason === 'planned_off';
  const noData = c.tonnes.reason === 'no_data';
  const coverageOk = c.coverage.every((cv) => cv.status === 'complete');
  const coverageSummary = c.coverage.map((cv) => `${FIELD_LABELS[cv.field] ?? cv.field} ${cv.received}/${cv.expected}`).join(' · ');
  const coreSources = sources.filter((s) => !s.isContextOnly);
  const sourceIssue = coreSources.find((s) => s.status === 'delayed' || s.status === 'error' || s.status === 'partial');
  const inScope = c.machines.filter((m) => m.inScope);
  const scopeShort = r.scope === 'all' ? `alle ${c.machines.length} maskiner` : `${inScope.length} maskiner i produksjonsområdet`;
  const co2Note = c.co2.source === 'unavailable' ? undefined : c.co2.source === 'estimated' ? `Estimert: diesel × ${fmtNumber(c.co2.factor, 2)} ${c.co2.factorUnit} (forutsetning)` : c.co2.source === 'reported' ? 'Rapportert av telematikk' : `Blandet: ${PROVENANCE_LABELS.reported} + ${PROVENANCE_LABELS.estimated}`;

  return (
    <Panel data-testid="oversikt-production-secondary-card">
      <PanelHeader
        eyebrow="Sekundært"
        title="Produksjonsanalyse"
        description={<span data-testid="overview-lead">{formatRange(r.fromDate, r.toDate)} mot {formatRange(r.compare.fromDate, r.compare.toDate)} · {scopeShort}</span>}
        actions={
          <>
            <div data-testid="coverage-summary">
              <CoverageStatus ok={coverageOk && !sourceIssue} summary={coverageSummary} sources={coreSources} onClick={() => navigate(linkTo('/datakilder'))} />
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to={linkTo('/produksjon')} data-testid="oversikt-production-open-link">
                Åpne Produksjon <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </Button>
          </>
        }
      />

      {(plannedOff || noData || (!c.basisComplete && !plannedOff && !noData) || c.fractionFilterActive) && (
        <div className="space-y-3 px-4 pt-4 sm:px-5">
          {plannedOff && <EmptyState type="planned_off" title="Ingen planlagt produksjon" description={`${formatRange(r.fromDate, r.toDate)} er planlagt driftsfri. Velg en annen periode i Produksjon.`} onReset={r.reset} className="py-8" />}
          {noData && <EmptyState type="no_results" title="Ingen data i valgt periode" description={`Ingen produksjons- eller maskinposter for ${formatRange(r.fromDate, r.toDate)}. Datavinduet er 07.07.2026 – 04.10.2026.`} onReset={r.reset} className="py-8" />}
          {!c.basisComplete && !plannedOff && !noData && (
            <Note tone="amber" data-testid="incomplete-basis-note">
              <strong>Ufullstendig grunnlag.</strong> {c.missingFields.map((f) => FIELD_LABELS[f] ?? f).join(', ')} mangler i beregningsområdet. Berørte forholdstall vises som utilgjengelige. Produksjonstallene er komplette.
            </Note>
          )}
          {c.fractionFilterActive && <Note tone="amber">Fraksjonsfilter er aktivt. Forholdstall per tonn er slått av fordi maskinforbruket ikke er fordelt per fraksjon.</Note>}
        </div>
      )}

      {/* Kompakt rad: tre forholdstall som alltid vises */}
      <div className="grid grid-cols-1 divide-y divide-[var(--hr-hairline)] sm:grid-cols-3 sm:divide-x sm:divide-y-0" data-testid="oversikt-production-summary">
        <InlineMetric metric={c.tonnes} row={cmp.byKey.tonnes} invertGood onExplain={() => setExplainKey('tonnes')} testKey="summary-tonnes" />
        <InlineMetric metric={c.dieselPerTonne} row={cmp.byKey.dieselPerTonne} onExplain={() => setExplainKey('dieselPerTonne')} testKey="summary-diesel-per-tonne" />
        <InlineMetric metric={c.idleShare} row={cmp.byKey.idleShare} onExplain={() => setExplainKey('idleShare')} testKey="summary-idle-share" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--hr-hairline)] px-4 py-2 sm:px-5">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-controls="production-analysis-body"
          className="inline-flex h-10 items-center gap-1.5 rounded-[8px] px-2 text-sm font-medium text-[var(--hr-heading)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]"
          data-testid="oversikt-production-toggle"
        >
          <ChevronDown className={cn('h-4 w-4 text-[var(--hr-muted)] transition-transform duration-150', expanded && 'rotate-180')} aria-hidden="true" />
          {expanded ? 'Skjul full analyse' : 'Vis full analyse'}
        </button>
        <span className="text-[13px] text-[var(--hr-muted-2)]">Nøkkeltall, trend, observasjoner og maskintabell · periode og scenario endres i Produksjon</span>
      </div>

      {expanded && (
        <div id="production-analysis-body">
          <ProductionAnalysisBody r={r} c={c} cmp={cmp} chartDays={chartDays} sparks={sparks} findings={findings} machinesById={machinesById} showCosts={showCosts} role={role} co2Note={co2Note} setExplainKey={setExplainKey} />
        </div>
      )}

      <MetricExplainDialog metric={explainMetric} result={c} open={!!explainMetric} onClose={() => setExplainKey(null)} />
    </Panel>
  );
}
